/* <pufferfish-logo> — cursor-tracking logomark. No dependencies, no build step.
   <script src="pufferfish-logomark.js" defer></script>
   <pufferfish-logo height="48"></pufferfish-logo>

   Attributes (all optional):
     height   px height of the mark                        default 48
     src      path to pufferfish.svg                       default "pufferfish.svg"
     eye      path to eye.svg                              default "eye.svg"
     reach    viewport fraction for a full look (0.15–1.5) default 0.5
     ease     follow lag, higher = snappier (0.02–0.5)     default 0.14
     travel   how far the pupil roams (1 = flush in socket)default 1.9
     tilt     body lean toward the cursor, 0 = off          default 1
     facing   "right" (mirrored) | "left" (original art)   default "right"
     blink    "off" to disable                             default on
*/
(function () {
  if (customElements.get("pufferfish-logo")) return;

  // Geometry from the artwork, in pufferfish.svg viewBox units (3669 x 2916).
  const VB_W = 3669, VB_H = 2916;
  const EYE_CX = 882.982, EYE_CY = 1166.63, EYE_RX = 199, EYE_RY = 205.5;
  const PUPIL_RATIO = 0.608; // pupil size as a fraction of the socket box
  const BODY = "#FF1759";    // body colour, used for the eyelids

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

  class PufferfishLogo extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      const num = (a, d) => { const v = parseFloat(this.getAttribute(a)); return isNaN(v) ? d : v; };
      this.cfg = {
        reach: num("reach", 0.5),
        ease: num("ease", 0.14),
        travel: num("travel", 1.9),
        tilt: num("tilt", 1),
        blink: this.getAttribute("blink") !== "off"
      };
      const h = num("height", 48);
      const src = this.getAttribute("src") || "pufferfish.svg";
      const eye = this.getAttribute("eye") || "eye.svg";
      const flip = this.getAttribute("facing") !== "left";
      this.restX = flip ? -0.62 : 0.62;

      const cw = VB_W, ch = VB_H, ox = 0, oy = 0;
      const scale = h / ch;                        // px per viewBox unit

      const root = this.shadowRoot || this.attachShadow({ mode: "open" });
      root.innerHTML =
        '<style>:host{display:inline-block;line-height:0}' +
        'img{user-select:none;-webkit-user-drag:none}</style>' +
        '<div id="mark" part="mark" style="position:relative;width:' + (cw * scale) + 'px;height:' + h + 'px;transform-origin:50% 72%">' +
          '<img src="' + src + '" alt="" draggable="false" style="position:absolute;left:' + (-ox * scale) + 'px;top:' + (-oy * scale) + 'px;width:' + (VB_W * scale) + 'px;height:' + (VB_H * scale) + 'px' + (flip ? ';transform:scaleX(-1)' : '') + '">' +
          '<div id="socket" style="position:absolute;left:' + (((flip ? VB_W - EYE_CX : EYE_CX) - ox) * scale) + 'px;top:' + ((EYE_CY - oy) * scale) + 'px;width:' + (EYE_RX * 2 * scale) + 'px;height:' + (EYE_RY * 2 * scale) + 'px;transform:translate(-50%,-50%)">' +
            '<img id="pupil" src="' + eye + '" alt="" draggable="false" style="position:absolute;left:50%;top:50%;width:' + (PUPIL_RATIO * 100) + '%;height:' + (PUPIL_RATIO * 100) + '%;transform:translate(-50%,-50%)">' +
            '<div style="position:absolute;inset:-3%;border-radius:50%;overflow:hidden;pointer-events:none">' +
              '<div id="lidTop" style="position:absolute;left:-35%;width:170%;height:170%;top:0;border-radius:50%;background:' + BODY + ';transform:translateY(-101%)"></div>' +
              '<div id="lidBot" style="position:absolute;left:-35%;width:170%;height:170%;bottom:0;border-radius:50%;background:' + BODY + ';transform:translateY(101%)"></div>' +
            '</div>' +
          '</div>' +
        '</div>';

      this.socket = root.getElementById("socket");
      this.mark = root.getElementById("mark");
      this.pupil = root.getElementById("pupil");
      this.lidTop = root.getElementById("lidTop");
      this.lidBot = root.getElementById("lidBot");

      root.querySelectorAll('img').forEach((image) => {
        image.addEventListener('error', () => { this.mark.style.visibility = 'hidden'; this.setAttribute('data-image-failed', ''); });
      });
      this.pos = { x: 0, y: 0 };
      this.target = { x: 0, y: 0 };
      this.lean = { x: 0, y: 0 };
      this.leanTarget = { x: 0, y: 0 };
      this.pointer = null;
      this.closedAmt = 0;
      this.blinkStart = -1;
      this.queued = 0;
      this.nextBlink = performance.now() + 2200 + Math.random() * 2600;

      this._move = (e) => { this.pointer = { x: e.clientX, y: e.clientY }; };
      this._leave = () => { this.pointer = null; };
      window.addEventListener("pointermove", this._move, { passive: true });
      document.addEventListener("pointerleave", this._leave);
      window.addEventListener("blur", this._leave);
      this._motionChange = () => {};
      document.addEventListener("miniami:motion-change", this._motionChange);
      this._connectionChange = () => {};
      navigator.connection?.addEventListener?.("change", this._connectionChange);

      // Pause when scrolled out of view, so the loop costs nothing off-screen.
      this._io = new IntersectionObserver((es) => { this.visible = es[0].isIntersecting; });
      this._io.observe(this);
      this.visible = true;

      this._tick = this._tick.bind(this);
      this.raf = requestAnimationFrame(this._tick);
    }

    disconnectedCallback() {
      window.removeEventListener("pointermove", this._move);
      document.removeEventListener("pointerleave", this._leave);
      window.removeEventListener("blur", this._leave);
      document.removeEventListener("miniami:motion-change", this._motionChange);
      navigator.connection?.removeEventListener?.("change", this._connectionChange);
      if (this._io) this._io.disconnect();
      cancelAnimationFrame(this.raf);
      this._connected = false;
    }

    _isMotionPaused() {
      // Only the actual Landing footer uses the authored reduced-motion path
      // below: resting gaze + open lids. Other consumers retain their gates.
      if (this.closest('.miniami-footer--landing')) return document.hidden;
      const forcedStatic = reduce.matches
        || navigator.connection?.saveData === true
        || document.hidden
        || document.documentElement?.dataset?.miniamiMotionForcedStatic === "true"
        || window.MiniamiMotion?.isForcedStatic?.() === true;
      return forcedStatic || window.MiniamiMotion?.isPaused?.() === true;
    }

    _tick(now) {
      this.raf = requestAnimationFrame(this._tick);
      if (!this.visible || this._isMotionPaused()) return;
      const c = this.cfg;
      const r = this.socket.getBoundingClientRect();
      if (r.width > 0) {
        const maxX = (r.width * (1 - PUPIL_RATIO) / 2) * c.travel;
        const maxY = (r.height * (1 - PUPIL_RATIO) / 2) * c.travel;
        if (this.pointer && !reduce.matches) {
          const dx = this.pointer.x - (r.left + r.width / 2);
          const dy = this.pointer.y - (r.top + r.height / 2);
          const dist = Math.hypot(dx, dy) || 1;
          const reach = Math.max(120, Math.min(innerWidth, innerHeight) * c.reach);
          const k = Math.min(1, dist / reach);
          this.target.x = (dx / dist) * maxX * k;
          this.target.y = (dy / dist) * maxY * k;
          this.leanTarget.x = (dx / dist) * k;
          this.leanTarget.y = (dy / dist) * k;
        } else {
          this.target.x = maxX * this.restX;  // rest: up, over the body
          this.target.y = -maxY * 0.62;
          this.leanTarget.x = 0;
          this.leanTarget.y = 0;
        }
        if (!this.seeded) { this.seeded = true; this.pos.x = this.target.x; this.pos.y = this.target.y; }
        const e = Math.min(1, Math.max(0.01, c.ease));
        this.pos.x += (this.target.x - this.pos.x) * e;
        this.pos.y += (this.target.y - this.pos.y) * e;
        // Body leans a touch toward the cursor, slower than the eye.
        this.lean.x += (this.leanTarget.x - this.lean.x) * e * 0.4;
        this.lean.y += (this.leanTarget.y - this.lean.y) * e * 0.4;
        const t = c.tilt;
        this.mark.style.transform =
          "rotate(" + (this.lean.x * 0.85 * t).toFixed(3) + "deg) translate(" +
          (this.lean.x * 0.5 * t).toFixed(3) + "%," + (this.lean.y * 0.42 * t).toFixed(3) + "%)";
        const pull = 1 - this.closedAmt * 0.62; // tuck in while the lid is down
        this.pupil.style.transform =
          "translate(-50%,-50%) translate(" + (this.pos.x * pull).toFixed(2) + "px," + (this.pos.y * pull).toFixed(2) + "px)";
      }

      let closed = 0;
      if (c.blink && !reduce.matches) {
        if (this.blinkStart < 0 && now >= this.nextBlink) {
          this.blinkStart = now;
          this.blinkSpeed = 0.88 + Math.random() * 0.3;
          if (this.queued > 0) this.queued--; else this.queued = Math.random() < 0.22 ? 1 : 0;
        }
        if (this.blinkStart >= 0) {
          const s = this.blinkSpeed || 1;
          const CLOSE = 80 * s, HOLD = 45 * s, OPEN = 190 * s;
          const t = now - this.blinkStart;
          if (t >= CLOSE + HOLD + OPEN) {
            this.blinkStart = -1;
            this.nextBlink = this.queued > 0 ? now + 150 : now + 2800 + Math.random() * 4600;
          } else if (t < CLOSE) { const u = t / CLOSE; closed = u * u * (3 - 2 * u); }
          else if (t < CLOSE + HOLD) { closed = 1; }
          else { const u = (t - CLOSE - HOLD) / OPEN; closed = 1 - u * u * (3 - 2 * u); }
        }
      }
      this.closedAmt = closed;
      this.lidTop.style.transform = "translateY(" + (-101 + closed * 74.5).toFixed(2) + "%)";
      this.lidBot.style.transform = "translateY(" + (101 - Math.max(0, closed * 1.3 - 0.3) * 14).toFixed(2) + "%)";
    }
  }

  customElements.define("pufferfish-logo", PufferfishLogo);
})();
